import React from "react";
import {
  Stack,
  Checkbox,
  Typography,
  TableRow,
  TableCell,
} from "@mui/material";
import { useFormContext } from "react-hook-form";

import { useStationListWithCache } from "../../components/StationHookFormSelect";
import { HubIcon, HubHookFormDatePicker } from "../../components";

import WorkOrderContextMenu from "./WorkOrderContextMenu";
import WorkOrderRoutingSubRow from "./WorkOrderRoutingSubRow";

import { RouteCell } from "./WorkOrderRoutingEditRow";
import { formatDateMmDdYyyy } from "../../helpers/dateHelper";

import { handleReschedule } from "./moveHelper";
import { condition, onDateChange } from "./helper";
import { useEmployeeScanContext } from "../../hooks/contexts/EmployeeScanContextProvider";

const TableSx = {
  padding: "0px !important",
  width: "150px",
  ".MuiInputBase-root": {
    padding: "8px 12px !important",
  },
  ".MuiInputBase-root, input": {
    borderRadius: 0,
  },
  fieldset: {
    borderWidth: "0px",
  },
};

const WorkOrderRoutingRow = ({
  item,
  idx,
  open,
  handleRowClick = () => {},
  setRouteId = () => {},
  showQuantity = false,
  prefix,
  ...rest
}) => {
  const { stations } = useStationListWithCache();
  const station = stations?.find((s) => s.id === item?.stationId);

  const { getValues, setValue } = useFormContext();

  const boxSx = {
    height: "100%",
    width: "100%",
    borderRight: "1px solid",
    borderColor: "hubColors.greyLight",
    alignItems: "center",
    justifyContent: "center",
  };

  const currentRoute = getValues("routeId");

  const isCurrent = item?.routeId === currentRoute;
  const isDone = !!item?.endDate;
  const isOpen = open === item?.routeId;
  const hasStarted = !!item?.startDate;

  const { scannedEmployee, canEditLabor } = useEmployeeScanContext();
  const laborUserIds = item?.laborUserIds ?? [];
  const isGreyedOut =
    laborUserIds.length === 0 ||
    (!!scannedEmployee &&
      !laborUserIds.some((userId) => canEditLabor?.(userId)));

  const conditionProps = condition({ ...item, isCurrent });

  const handleOnDateChange = (isEnd) => {
    onDateChange(prefix, getValues, setValue, isEnd);
    handleReschedule(idx + 1, getValues, setValue);
  };

  return (
    <>
      <TableRow
        backgroundColor="hubColors.white"
        {...rest}
        {...conditionProps}
        sx={{ ...conditionProps.sx, ...(isGreyedOut && { backgroundColor: "hubColors.greyLightest" }) }}
      >
        <TableCell variant="small">
          <Stack
            sx={{ ...boxSx, cursor: "pointer" }}
            onClick={() => handleRowClick(item.routeId)}
          >
            <HubIcon
              icon={isOpen ? "ChevronDown" : "ChevronRight"}
              size="12px"
            />
          </Stack>
        </TableCell>

        <TableCell sx={{ minWidth: "250px" }}>
          <Stack direction="row" gap="10px" alignItems="center">
            <Checkbox sx={{ width: "14px", padding: 0 }} checked={isDone} />
            <Typography variant="caption">{station?.label}</Typography>
          </Stack>
        </TableCell>
        {showQuantity && (
          <RouteCell value={item?.quantity} sx={{ width: "150px" }} />
        )}
        <RouteCell value={item?.completed} sx={{ width: "150px" }} />
        <RouteCell value={item?.scrapped} sx={{ width: "150px" }} />
        <TableCell sx={TableSx}>
          <HubHookFormDatePicker
            name={`${prefix}.plannedStartDate`}
            onChange={() => handleOnDateChange(false)}
            shouldUnregister={false}
            useSetValue
            required
          />
        </TableCell>
        <TableCell sx={TableSx}>
          <HubHookFormDatePicker
            name={`${prefix}.plannedEndDate`}
            onChange={() => handleOnDateChange(true)}
            shouldUnregister={false}
            useSetValue
            required
          />
        </TableCell>
        <RouteCell
          value={formatDateMmDdYyyy(item?.startDate)}
          defaultValue="MM/DD/YYYY"
          sx={{ width: "150px" }}
        />
        <RouteCell
          value={formatDateMmDdYyyy(item?.endDate)}
          defaultValue="MM/DD/YYYY"
          sx={{ width: "150px" }}
        />
        <TableCell variant="small">
          <WorkOrderContextMenu
            id={idx}
            routeId={item?.routeId}
            disabled={hasStarted}
            setRouteId={setRouteId}
          />
        </TableCell>
      </TableRow>
      {isOpen && item?.routeId && (
        <WorkOrderRoutingSubRow routeId={item?.routeId} isOpen={isOpen} />
      )}
    </>
  );
};

export default WorkOrderRoutingRow;
